import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';

// Components
import Button from '@atoms/Button';
import LoadingSpinnerIcon from '@icons/LoadingSpinnerIcon';
import EmptyStateContainer from '@molecules/EmptyStateContainer';
import TrackPageView from '@tracking/TrackPageView';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionHeader from '../../../_molecules/SectionHeader';
import ConnectionsMobileSubnav from './ConnectionsMobileSubnav';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { REMOVE_SAVED_SEARCH } from '@general/graphql/saved-searches.graph';
import useDocumentTitle from '@hooks/useDocumentTitle';
import useIcscTracking from '@tracking/useIcscTracking';

import {
    BodyLarge,
    DivWithShadowAlternate,
    H3,
    H4,
    LinkSmall,
    SectionWrapperInner,
    SectionWrapperOuter,
} from '@styled';
import Match from './Match';
import * as Tabs from '../../../_molecules/TabGroup';
import useSavedSearchesWithMatches from '../../../../hooks/useSavedSearchesWithMatches';

const MatchCriteriaContainer = styled.div`
    align-items: center;
    border-top: 1px solid ${colors.lightGray};
    display: flex;
    justify-content: space-between;
    padding-top: 2rem;
    padding-bottom: 1.5rem;
`;

const ButtonContainer = styled.div`
    border-top: 1px solid ${colors.lightGray};
    display: flex;
    justify-content: center;
    padding-top: 2rem;

    @media ${mediaQueries.md} {
        display: block;
    }

    .btn {
        padding: 1rem 2rem;

        @media ${mediaQueries.md} {
            padding: 1rem 3rem;
        }
    }
`;

export const MyMatchesPage = () => {
    const history = useHistory();
    const { trackEvent } = useIcscTracking();
    const [newMatchesOnly, setNewMatchesOnly] = useState(false);
    useDocumentTitle('My Matches');
    const { loading, savedSearchesWithMatches } = useSavedSearchesWithMatches();

    const totalNewMatches = savedSearchesWithMatches.reduce((acc, search) => {
        return (acc += search.matches[0].hits.length);
    }, 0);

    const [removeSavedSearch] = useMutation(REMOVE_SAVED_SEARCH, {
        update: (cache, result) => {
            cache.evict({ id: cache.identify(result.data.removeSavedSearch) });
        },
    });

    const deleteSavedSearch = id => {
        removeSavedSearch({ variables: { id } });
    };

    const handleClickSavedSearch = savedSearch => {
        trackEvent({
            excludeService: ['Algolia'],
            click: {
                target: 'clicked_on_match_criteria',
                value: 'Clicked on Match Criteria',
            },
        });
    };

    return (
        <ErrorHandler>
            <TrackPageView>
                <ConnectionsMobileSubnav />

                <SectionWrapperOuter>
                    <SectionWrapperInner className="fade-in-fast">
                        <SectionHeader title="My ICSC Connections" />

                        <div className="flex justify-content-between mb-1.5rem">
                            <SectionBreadcrumbs className="mt-2">
                                <SectionBreadcrumb route="/my-icsc/connections">Connections</SectionBreadcrumb>
                                <SectionBreadcrumb isActive>My Matches</SectionBreadcrumb>
                            </SectionBreadcrumbs>

                            <div>
                                <LinkSmall
                                    href="https://view.ceros.com/icsc/icsc-networking-platfrom/p/1"
                                    target="_blank"
                                >
                                    {'How to use the Networking Platform>>'}
                                </LinkSmall>
                            </div>
                        </div>

                        <H3 className="mt-2 mb-3">My Matches</H3>

                        <BodyLarge className="mb-1.5rem">
                            Get the most out of ICSC networking by leveraging contact recommendations. Create Match
                            criteria based on what you're looking for and ICSC will connect you to the right members.
                        </BodyLarge>

                        {loading && (
                            <DivWithShadowAlternate className="p-4">
                                <div className="d-flex align-items-center justify-content-center p-4">
                                    <LoadingSpinnerIcon fill="#545454" style={{ marginRight: '10px' }} />
                                </div>
                            </DivWithShadowAlternate>
                        )}

                        {!loading && savedSearchesWithMatches?.length > 0 && (
                            <>
                                <H4 className="mb-1.5rem">My Saved Match Criteria</H4>

                                <Tabs.TabGroup>
                                    <Tabs.Tab active={!newMatchesOnly} onClick={() => setNewMatchesOnly(false)}>
                                        All
                                    </Tabs.Tab>
                                    <Tabs.Tab active={newMatchesOnly} onClick={() => setNewMatchesOnly(true)}>
                                        New Matches{' '}
                                        {totalNewMatches > 0 && (
                                            <Tabs.TabCountChip>{totalNewMatches}</Tabs.TabCountChip>
                                        )}
                                    </Tabs.Tab>
                                </Tabs.TabGroup>

                                <DivWithShadowAlternate className="p-4">
                                    {newMatchesOnly && totalNewMatches === 0 && (
                                        <BodyLarge>
                                            You currently don’t have any new matches. Create new match criteria below.
                                        </BodyLarge>
                                    )}
                                    {savedSearchesWithMatches.map(savedSearch => (
                                        <Match
                                            key={savedSearch.search.id}
                                            newMatchesOnly={newMatchesOnly}
                                            savedSearch={savedSearch.search}
                                            handleClickSavedSearch={handleClickSavedSearch}
                                            deleteSavedSearch={deleteSavedSearch}
                                            matches={savedSearch.matches[0].hits}
                                        />
                                    ))}
                                </DivWithShadowAlternate>
                                <ButtonContainer>
                                    <BodyLarge className="mb-2rem">
                                        Answer a few questions to unlock personalized connection recommendations.
                                    </BodyLarge>
                                    <Link to="/my-icsc/connections/matches/new">
                                        <Button>Create New Match Criteria</Button>
                                    </Link>
                                </ButtonContainer>
                            </>
                        )}

                        {!loading && !savedSearchesWithMatches?.length && (
                            <EmptyStateContainer
                                Body={() => (
                                    <>Tell us what you're looking for and we'll create customized recommendations.</>
                                )}
                                header="Unlock Connection Recommendations"
                                ctaText="Create Your First Set of Match Criteria"
                                ctaHref="/my-icsc/connections/matches/new"
                                onCTAClick={e => {
                                    e.preventDefault();
                                    history.push('/my-icsc/connections/matches/new');
                                }}
                                imageSrc="/assets/modules/images/MyMatchesEmptyStatePlaceholder.png?v=1644955589"
                                imageAlt="No match criteria"
                            />
                        )}
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};
